import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconEdit, IconPlus } from '../../../common/icons';
import { useSemenList } from '../contexts/semen';
import SectionTabs from '../../navigation/section-tabs';
import { ANIMAL_TABS } from '../../navigation/tabs';

import '../../../common/styles/catalog.css';

const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };
const ORIGIN_LABEL = { national: 'Nacional', imported: 'Importado' };

export default function SemenOverview() {
    const [params, setParams] = useSearchParams();
    const urlSearch = params.get('q') || '';
    const urlOrigin = params.get('origin') || '';

    const [search, setSearch] = useState(urlSearch);
    const [debounced, setDebounced] = useState(urlSearch);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debounced) next.set('q', debounced); else next.delete('q');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const setOrigin = (value) => {
        const next = new URLSearchParams(params);
        if (value) next.set('origin', value); else next.delete('origin');
        setParams(next, { replace: true });
    };

    const filters = useMemo(() => {
        const out = {};
        if (debounced) out.q = debounced;
        if (urlOrigin) out.origin = urlOrigin;
        return out;
    }, [debounced, urlOrigin]);

    const { data, isLoading, isFetching, error } = useSemenList(filters);
    const items = data?.items ?? [];
    const total = data?.pagination?.total ?? items.length;

    return (
        <div className="cat-overview">
            <SectionTabs tabs={ANIMAL_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Semen</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="semen" action="write">
                    <Link to="/admin/semen/new" className="cat-overview__add">
                        <IconPlus />
                        <span>Nuevo semen</span>
                    </Link>
                </Gate>
            </header>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar por código, nombre o padre…"
                        aria-label="Buscar semen por código, nombre o padre"
                    />
                </div>
                <select className="cat-overview__filter" value={urlOrigin} onChange={(e) => setOrigin(e.target.value)} aria-label="Filtrar por origen">
                    <option value="">Todos los orígenes</option>
                    <option value="national">Nacional</option>
                    <option value="imported">Importado</option>
                </select>
            </div>

            {error && <div className="cat-overview__error">{error.message || 'No se pudo cargar el semen'}</div>}
            {total > items.length && <p className="cat-overview__note">Mostrando los primeros {items.length} de {total} registros. Refiná la búsqueda o el filtro.</p>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando semen…</div>
            ) : !items.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">
                        {debounced || urlOrigin ? 'Ningún registro coincide con el filtro.' : 'Aún no hay semen registrado.'}
                    </p>
                    {!debounced && !urlOrigin && (
                        <Gate domain="semen" action="write">
                            <Link to="/admin/semen/new" className="cat-overview__add">
                                <IconPlus /><span>Registrar el primer semen</span>
                            </Link>
                        </Gate>
                    )}
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead>
                            <tr>
                                <th>Código</th>
                                <th>Nombre</th>
                                <th>Raza</th>
                                <th>Origen</th>
                                <th>Estado</th>
                                <th className="cat-table__actions-col" aria-label="Acciones" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((s) => (
                                <tr key={s.id} className="cat-table__row">
                                    <td><Link to={`/admin/semen/${s.id}`} className="cat-table__name">{s.code}</Link></td>
                                    <td>{s.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{s.race?.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{ORIGIN_LABEL[s.origin] || <span className="cat-table__empty">—</span>}</td>
                                    <td><span className={`cat-badge cat-badge--${s.status}`}>{STATUS_LABEL[s.status] || '—'}</span></td>
                                    <td className="cat-table__actions">
                                        <Gate domain="semen" action="write">
                                            <Link to={`/admin/semen/${s.id}/edit`} className="cat-table__edit" aria-label={`Editar ${s.code}`} title="Editar">
                                                <IconEdit />
                                            </Link>
                                        </Gate>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
